import { CBInputMethod } from '@cb/apricot'
import { InputDropdown } from '@cb/apricot-react'
import { CBUtils } from '@cb/apricot'
import React, { useEffect, useRef, useState, useCallback } from 'react'

interface SchoolSelectProps {
  clearInputOnSelect?: boolean
  data: any;
  dataItems?: any[]
  disabled?: boolean
  id: string
  label: string
  onChange?: (value: string) => void
  onClear?: () => void
  onSelect?: (label: string, obj: any) => void
  scrollLimit?: number
  startingDataItems?: any[]
}

function debounce(func: (...args: any[]) => void, delay: number) {
  let timeoutId: NodeJS.Timeout
  return (...args: any[]) => {
    clearTimeout(timeoutId)
    timeoutId = setTimeout(() => func(...args), delay)
  }
}

export const SchoolSelect = ({
  clearInputOnSelect = true,
  data,
  disabled,
  id,
  label,
  onChange,
  onClear,
  onSelect,
  scrollLimit = 100,
}: SchoolSelectProps) => {
  const [schools, setSchools] = useState<any>([])
  const [filteredSchools, setFilteredSchools] = useState<any>([])
  const [inputValue, setInputValue] = useState<string>('')
  const [dropdownIcon] = useState<boolean>(() => filteredSchools.some((item) => item.icon))
  const inputDropdownRef = useRef<any>(null)
  const pluginRef = useRef<any>(null)

  const handleFilter = useCallback((value: string) => {
    if (value.length >= 3) {
      const filtered = schools.filter((school) =>
        school.school.toLowerCase().includes(value.toLowerCase())
      )
      setFilteredSchools(filtered)
    } else {
      setFilteredSchools([])
    }
  }, [schools])

  const debouncedHandleFilter = useCallback(
    debounce((value: string) => handleFilter(value), 300),
    [handleFilter]
  )

  useEffect(() => {
    const formattedSchools = data.map((school) => ({
      value: school.aicode,
      label: school.school,
      ...school,
    }))
    setSchools(formattedSchools)
    setFilteredSchools(formattedSchools.slice(0, 5))
  }, [])

  useEffect(() => {
    if (pluginRef.current) {
      pluginRef.current.updateDataItems(filteredSchools)
    }
  }, [filteredSchools])

  useEffect(() => {
    CBInputMethod()
    pluginRef.current = MSSSAutocomplete({
      elem: inputDropdownRef.current,
      data: filteredSchools,
    })

    return () => {
      if (pluginRef.current) {
        pluginRef.current.destroy()
      }
    }
  }, [])

  useEffect(() => {
    if (disabled) {
      setInputValue('')
      if (inputDropdownRef.current) {
        inputDropdownRef.current.setAttribute('data-cb-value', '')
        const input = inputDropdownRef.current.querySelector(`#${id}-input`)
        if (input) {
          input.setAttribute('data-cb-value', '')
        }
      }
      if (pluginRef.current) {
        pluginRef.current.clearValue()
      }
    }
  }, [disabled, id])

  const handleSelect = (label: string, obj: any) => {
    onSelect?.(label, obj)
  }

  const handleChange = (value: string) => {
    setInputValue(value)
    debouncedHandleFilter(value)
    onChange?.(value)
  }

  const handleClear = () => {
    setInputValue('')
    setFilteredSchools(schools.slice(0, 5))
    onClear?.()
  }

  return (
    <InputDropdown
      callBack={(e: any, value: string, obj: any) => {
        const parent = CBUtils.getClosest(e.target, 'li')

        const selectedItem = {
          school: parent.getAttribute('data-cb-obj-label'),
          aiCode: parent.getAttribute('data-cb-obj-value'),
        }

        setInputValue(clearInputOnSelect ? '' : value)
        handleSelect(value, selectedItem)

        if (clearInputOnSelect) {
          inputDropdownRef.current?.setAttribute('data-cb-value', '')
          inputDropdownRef.current?.querySelector(`#${id}-input`)?.setAttribute('data-cb-value', '')
          pluginRef.current?.clearValue()
        }
      }}
      dataItems={filteredSchools}
      data-test-id="school-select-input"
      // startingDataItems={schools.slice(0, 5)} // Limit starting items to 5
      defaultItems={!disabled}
      disabled={disabled}
      dropdownIcon={dropdownIcon}
      dropdownId={`${id}-input-dropdown`}
      dropdownOverlay={true}
      event="msss_updateData"
      icon="search"
      inputId={`${id}-input`}
      label={label}
      labelId={`${id}-input-label`}
      onChange={(e: any) => {
        setInputValue(e.target.value)
        handleChange(e.target.value)
      }}
      onClear={() => {
        setInputValue('')
        handleClear()
      }}
      ref={inputDropdownRef}
      scrollLimit={scrollLimit}
      value={inputValue}
      className="school-select-half-width"
      condensed={true}
    />
  )
}

// temporary until migrating to React
const MSSSAutocomplete = (data: any = {}) => {
  const defaultData = {
    elem: null,
    data: [],
  }

  data = { ...defaultData, ...data }

  const { elem } = data
  let { data: dataList } = data
  let curValue = ''

  const autocomplete = (value: string) => {
    curValue = value
    const result: any[] = []
    if (value !== '' && value !== null) {
      dataList.forEach((item: any) => {
        if (item.label.toLowerCase().includes(value.toLowerCase())) {
          result.push(item)
        }
      })
    }

    const customEvent: any = new CustomEvent('msss_updateData')
    customEvent.data = result

    elem?.dispatchEvent(customEvent)
  }

  const clearValue = () => {
    autocomplete('')
  }

  const init = () => {
    elem?.addEventListener('apricot_inputDropdown', (e: { data: { type: string; value: any } }) => {
      if (e.data && e.data.type === 'change') {
        if (e.data.value) {
          autocomplete(e.data.value)
        } else {
          autocomplete('')
        }
      }
    })
  }

  const updateDataItems = (dataItems: any[]) => {
    dataList = dataItems
    autocomplete(curValue)
  }

  const destroy = () => {
    if (elem?.elemFocusEvents === 'cb') {
      elem.elemFocusEvents = null
    }
    const customEvent: any = new CustomEvent('msss_updateData')
    customEvent.data = []

    elem?.dispatchEvent(customEvent)
  }

  if (elem?.elemFocusEvents !== 'cb') {
    init()
  }

  return {
    clearValue,
    destroy,
    updateDataItems,
  }
}
